// src/pages/Report.jsx
import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { KPI, EscalateButton } from "../components/UIBits";
import { formatNumber } from "../helpers/utils";

export default function ReportPage() {
  const {
    report,
    aiExecSummary,
    aiExecLoading,
    decisions,
    decisionsLoading,
    aiLoading,
    aiText,

    // ✅ ADD THESE if your outlet provides them (undefined is fine)
    teamsConnected,
    asanaConnected,
    slackConnected,
    googleConnected,
    microsoftConnected,
    calendarConnected,
  } = useOutletContext();

  // ✅ If ANY tool is connected, don’t show “connect” CTAs in decisions
  const anyToolConnected = !!(
    teamsConnected ||
    asanaConnected ||
    slackConnected ||
    googleConnected ||
    microsoftConnected ||
    calendarConnected
  );

  function DecisionItem({ text, k }) {
    const needsConnect = /\bconnect\b/i.test(text);

    return (
      <li key={k}>
        {text}
        {needsConnect && (
          <Link
            to="/control-room"
            className="btn primary"
            style={{ marginLeft: 8 }}
          >
            Connect tools
          </Link>
        )}
      </li>
    );
  }

  const totalDecisions =
    (decisions?.mission_critical?.length || 0) +
    (decisions?.this_week?.length || 0) +
    (decisions?.next_week?.length || 0);

  return (
    <section className="panel">
      <h2>Weekly Executive Report</h2>

      {/* KPIs */}
      <div className="grid3">
        <KPI label="Issues closed" value={report.kpis.issuesClosed} />
        <KPI
          label="Lead time (days)"
          value={report.kpis.leadTimeDays.toFixed(1)}
        />
        <KPI
          label="On-time releases"
          value={`${Math.round(report.kpis.onTimePct)}%`}
        />
        <KPI
          label="On-time tasks"
          value={`${Math.round(report.kpis.onTimeTasksPct)}%`}
        />
      </div>

      <div className="split">
        {/* LEFT COLUMN */}
        <div>
          <div className="panelSub">
            <h3>Quick Executive Summary</h3>
            <p className="para">
              {aiExecLoading ? (
                <span className="shimmerText">Generating…</span>
              ) : (
                aiExecSummary || "(no summary yet)"
              )}
            </p>
          </div>

          <div className="panelSub" style={{ marginTop: 12 }}>
            <div className="decisionsHeader">
              <h3>
                Decisions Needed{totalDecisions ? ` (${totalDecisions})` : ""}
              </h3>
              <Link className="btn success" to="/scheduler">
                Open Scheduler
              </Link>
            </div>

            {decisionsLoading ? (
              <p className="muted">
                <span className="shimmerText">AI is thinking…</span>
              </p>
            ) : (
              <>
                <h4 className="muted" style={{ marginTop: 8, color: "red" }}>
                  Mission critical
                </h4>
                {decisions.mission_critical?.length ? (
                  <ul className="bullets tight">
                    {decisions.mission_critical.map((d, i) => (
                      <DecisionItem text={d} k={`mc-${i}`} />
                    ))}
                  </ul>
                ) : (
                  <p className="muted">None flagged as mission-critical.</p>
                )}

                <h4 className="muted" style={{ marginTop: 14 }}>
                  This week
                </h4>
                {decisions.this_week?.length ? (
                  <ul className="bullets tight">
                    {decisions.this_week.map((d, i) => (
                      <li key={`tw-${i}`}>{d}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">No immediate decisions this week.</p>
                )}

                <h4 className="muted" style={{ marginTop: 14 }}>
                  Next week
                </h4>
                {decisions.next_week?.length ? (
                  <ul className="bullets tight">
                    {decisions.next_week.map((d, i) => (
                      <li key={`nw-${i}`}>{d}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">Nothing queued for next week yet.</p>
                )}
              </>
            )}
          </div>

          <div className="panelSub" style={{ marginTop: 12 }}>
            <h3>Risks & Owners</h3>
            {report.risks.length === 0 ? (
              <p className="muted">No risks above thresholds.</p>
            ) : (
              <ul className="list compact" style={{ marginTop: 8 }}>
                {report.risks.map((r, idx) => (
                  <li key={idx} className="listItem">
                    <div>
                      <div className="itemTitle">{r.title}</div>
                      <div className="muted">
                        Owner: {r.owner} • {r.ageDays}d old
                        {r.cost ? ` • $${formatNumber(r.cost)}` : ""}
                      </div>
                      <div className="para small">Action: {r.action}</div>
                    </div>
                    <EscalateButton owner={r.owner} text={r.action} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div>
          <div className="panelSub">
            <h3>AI Rewrite</h3>
            <div className="aiBox">
              {aiLoading ? (
                <div className="spinner" />
              ) : (
                <pre className="aiText">
                  {aiText ||
                    'Click "AI: Rewrite Summary" to generate an exec-ready blurb.'}
                </pre>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
